import React, { ReactElement, ReactNode } from 'react';

// UtilitiesF
import { createExtender } from '../../../lib/bemUtils/bem';

// Components
import DefinitionList from './DefinitionList';
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import { Heading } from '../Heading/Heading';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';

const descriptedDefinitionListExtender = createExtender('descripted-definition-list');

type Term = {
  term: string | ReactNode;
  description?: string | ReactElement | null;
  href?: string | null;
  extendedInfoSVG?: { helpText: string; tooltipPlacement: TooltipPlacement };
} | null;
type DescriptedDefinitionListProps = {
  headline?: string | { text: string; subheadline?: string | string[]; href?: string };
  headlineInvisible?: boolean;
  headlineHierarchy?: number;
  styling?: string;
  definitionTerms?: Term[];
};

/**
 * DefinitionList with basic style.
 * This DL will only need simple key-value pairs, cause we use them that much.
 */
export function DescriptedDefinitionList({
  headline,
  headlineInvisible,
  headlineHierarchy = 2,
  styling = 'normal',
  definitionTerms = [],
}: DescriptedDefinitionListProps) {
  function getSubHeadline() {
    if (typeof headline === 'string') {
      return null;
    }
    if (!headline?.subheadline) {
      return null;
    }

    const text = headline.subheadline;
    const subhead = typeof text === 'string' ? text : text.join(', ');
    return <p className="descripted-definition-list__subheadline">{subhead}</p>;
  }

  const getHeadline = () => {
    if (headline) {
      const headlineText = typeof headline !== 'string' ? headline.text : headline;
      const link = typeof headline !== 'string' && headline.href ? headline.href : null;
      return (
        <div className="descripted-definition-list__headblock">
          <Heading
            className={headlineInvisible ? 'invisible' : 'descripted-definition-list__headline'}
            hierarchy={headlineHierarchy}
          >
            {link
              ? (
                  <Hyperlink to={link} className="descripted-definition-list__link">
                    {headlineText}
                  </Hyperlink>
                )
              : (
                  headlineText
                )}
          </Heading>
          {getSubHeadline()}
        </div>
      );
    }
    return null;
  };

  const mappedDL = definitionTerms.map((definition) => {
    if (definition && definition.description) {
      return {
        term: definition.term,
        extendedInfoSVG: definition.extendedInfoSVG,
        class: 'definition-list__term',
        definitionDescriptions: [
          {
            description: definition.href
              ? (
                  <Hyperlink to={definition.href} className="definition-list__link">
                    {definition.description}
                  </Hyperlink>
                )
              : (
                  definition.description
                ),
            class: 'definition-list__description',
          },
        ],
      };
    }
    return null;
  });

  return (
    <div className={descriptedDefinitionListExtender([`descripted-definition-list--${styling}`])}>
      {getHeadline()}
      <DefinitionList className="definition-list" definitionTerms={mappedDL} />
    </div>
  );
}
